import * as React from 'react';
import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Popover, Select, MenuItem, FormControl } from '@material-ui/core';

// Generic interfaces for validator items
export interface IValidatorItem {
  status: string | number;
  decision?: string;
  async_task_status?: string | number;
  ai_result?: {
    decision?: string;
    selected_ref?: number;
    selected_refId?: number;
    unlinkable_concepts?: any[];
    [key: string]: any;
  };
  [key: string]: any;
}

export interface IValidatorDecisionConfig {
  key: string;
  label: string;
  color: string;
  // Optional: for decisions that need special handling
  showInProgress?: boolean;
}

export interface IValidatorStatusConfig {
  PENDING: string | number;
  NEEDS_USER_REVIEW: string | number;
  DONE: string | number;
}

export interface IValidatorAsyncTaskStatus {
  FAILED: string | number;
  DEAD_LETTER: string | number;
}

export interface IChangeGroup {
  ref: number;
  refId: number;
  count: number;
  conceptName?: string;
}

export interface IValidatorSummaryProps {
  // Data
  items: IValidatorItem[];
  uniqueItemCount?: number;

  // Configuration
  decisions: IValidatorDecisionConfig[];
  statusConfig: IValidatorStatusConfig;
  asyncTaskStatus: IValidatorAsyncTaskStatus;

  // Callbacks
  onFilterChange: (type: string, value: string) => void;
  activeFilter: { type: string; value: string } | null;
  onClearFilter: () => void;

  // Optional features
  enableChangeGrouping?: boolean;
  changeGroupComponent?: React.ComponentType<{ conceptRef: number; refId: number; courseId: number; showLabel?: boolean }>;
  courseId?: number;

  // Additional custom features
  customFeatures?: {
    cannotLink?: boolean;
    noDecision?: boolean;
  };

  // Custom colors
  colors?: {
    processing?: string;
    review?: string;
    error?: string;
    cannotLink?: string;
  };

  // When true, both processing and review badges are highlighted as active
  unifiedProcessingReviewFilter?: boolean;
}

const ValidatorSummary: React.FC<IValidatorSummaryProps> = ({
  items,
  uniqueItemCount,
  decisions,
  statusConfig,
  asyncTaskStatus,
  onFilterChange,
  activeFilter,
  onClearFilter,
  enableChangeGrouping = false,
  changeGroupComponent: ChangeGroupComponent,
  courseId = 0,
  customFeatures = {},
  colors: customColors = {},
  unifiedProcessingReviewFilter = false,
}) => {
  const [changeAnchorEl, setChangeAnchorEl] = useState<null | HTMLElement>(null);

  const defaultColors = {
    processing: '#9C27B0',
    review: '#FFA726',
    error: '#E53935',
    cannotLink: '#FF7043',
    ...customColors,
  };

  const { summary, changeGroups, completedCounts } = useMemo(() => {
    if (!items || !Array.isArray(items)) {
      const emptySummary: any = { 
        total: 0, 
        processing: 0, 
        review: 0, 
        error: 0,
        cannotLink: 0,
      };
      const emptyCompleted: any = {};
      decisions.forEach(d => {
        emptySummary[d.key] = 0;
        emptyCompleted[d.key] = 0;
      });
      return {
        summary: emptySummary,
        changeGroups: {},
        completedCounts: emptyCompleted,
      };
    }

    const summaryData: any = {
      total: uniqueItemCount !== undefined ? uniqueItemCount : items.length,
      processing: 0,
      review: 0,
      error: 0,
      cannotLink: 0,
    };
    
    const completedData: any = {
      error: 0, // Track error count for progress bar
    };
    
    // Initialize decision counts
    decisions.forEach(d => {
      summaryData[d.key] = 0;
      completedData[d.key] = 0;
    });
    
    // Add no_decision if needed
    if (customFeatures?.noDecision) {
      summaryData.noDecision = 0;
    }

    const changeGroupsData: Record<string, IChangeGroup> = {};

    items.forEach((item) => {
      // Count items with unlinkable concepts (if feature enabled)
      if (customFeatures?.cannotLink && item.ai_result?.unlinkable_concepts && item.ai_result.unlinkable_concepts.length > 0) {
        summaryData.cannotLink++;
      }
      
      // Count errors first
      if (
        item.async_task_status === asyncTaskStatus.FAILED ||
        item.async_task_status === asyncTaskStatus.DEAD_LETTER ||
        item.ai_result?.decision === 'error'
      ) {
        summaryData.error++;
        completedData.error++; // Also count for progress bar
        return;
      }

      // Count by status
      if (item.status === statusConfig.PENDING) {
        summaryData.processing++;
      } else if (item.status === statusConfig.NEEDS_USER_REVIEW) {
        summaryData.review++;
        // Count AI suggestions
        decisions.forEach(d => {
          if (item.ai_result?.decision === d.key) {
            summaryData[d.key]++;
          }
        });
        if (customFeatures?.noDecision && item.ai_result?.decision === 'no_decision') {
          summaryData.noDecision++;
        }
      } else if (item.status === statusConfig.DONE) {
        // Count AI suggestions for stats
        decisions.forEach(d => {
          if (item.ai_result?.decision === d.key) {
            summaryData[d.key]++;
          }
        });
        if (customFeatures?.noDecision && item.ai_result?.decision === 'no_decision') {
          summaryData.noDecision++;
        }
        
        // Count actual user decisions for progress bar
        decisions.forEach(d => {
          if (item.decision === d.key) {
            completedData[d.key]++;
          }
        });
      }

      // Group changes if enabled
      if (
        enableChangeGrouping &&
        (item.decision === 'change' ||
          (item.ai_result?.decision === 'change' && item.status !== statusConfig.DONE)) &&
        item.ai_result?.selected_ref &&
        item.ai_result?.selected_refId
      ) {
        const key = `${item.ai_result.selected_ref}_${item.ai_result.selected_refId}`;
        if (!changeGroupsData[key]) {
          changeGroupsData[key] = {
            ref: item.ai_result.selected_ref,
            refId: item.ai_result.selected_refId,
            count: 0,
          };
        }
        changeGroupsData[key].count++;
      }
    });

    return { 
      summary: summaryData,
      changeGroups: changeGroupsData,
      completedCounts: completedData,
    };
  }, [items, decisions, statusConfig, asyncTaskStatus, customFeatures, enableChangeGrouping]);

  const hasChangeGroups = Object.keys(changeGroups).length > 0;

  const handleChangeClick = (event: React.MouseEvent<HTMLElement>) => {
    if (hasChangeGroups) {
      setChangeAnchorEl(event.currentTarget);
    }
  };

  const handleChangeClose = () => {
    setChangeAnchorEl(null);
  };

  const changeOpen = Boolean(changeAnchorEl);

  const handleConceptFilterClick = (ref: number, refId: number) => {
    onFilterChange('change-concept', `${ref}_${refId}`);
    handleChangeClose();
  };

  const isFilterActive = (type: string, value: string) => {
    return activeFilter?.type === type && activeFilter?.value === value;
  };

  // Don't render anything if there are no items
  if (summary.total === 0) {
    return null;
  }

  // Calculate progress segments including errors
  const allSegments = [
    // Add error segment first if there are errors
    ...(completedCounts.error > 0 ? [{
      key: 'error',
      width: (completedCounts.error / summary.total) * 100,
      color: defaultColors.error,
    }] : []),
    // Then add decision segments
    ...decisions.map(d => ({
      key: d.key,
      width: summary.total > 0 ? (completedCounts[d.key] / summary.total) * 100 : 0,
      color: d.color,
    }))
  ].filter(s => s.width > 0);

  // Calculate cumulative offsets
  let cumulativeOffset = 0;
  const segmentsWithOffset = allSegments.map((s, index) => {
    const segment = { ...s, offset: cumulativeOffset };
    cumulativeOffset += s.width;
    
    // Determine position
    let position: 'first' | 'middle' | 'last' | 'only' = 'middle';
    if (allSegments.length === 1) position = 'only';
    else if (index === 0) position = 'first';
    else if (index === allSegments.length - 1) position = 'last';
    
    return { ...segment, position };
  });

  // Hide progress bar when uniqueItemCount is provided (multi-category counting mode)
  const showProgressBar = uniqueItemCount === undefined;

  return (
    <Container>
      {showProgressBar && (
        <ProgressBar>
          {summary.processing > 0 ? (
            <ProgressFill $width={100} $isProcessing={true} />
          ) : (
            <>
              {segmentsWithOffset.map(segment => (
                <ProgressSegment
                  key={segment.key}
                  $width={segment.width}
                  $color={segment.color}
                  $position={segment.position}
                  $offset={segment.offset}
                />
              ))}
            </>
          )}
        </ProgressBar>
      )}

      <StatsCard>
        <StatItem $clickable={true} onClick={onClearFilter} className={!activeFilter ? 'active' : ''}>
          <div className="count">{summary.total}</div>
          <div className="label">Total</div>
        </StatItem>

        {summary.processing > 0 && (
          <StatItem
            $color={defaultColors.processing}
            $clickable={summary.processing > 0}
            onClick={() => summary.processing > 0 && onFilterChange('status', 'processing')}
            className={isFilterActive('status', 'processing') || unifiedProcessingReviewFilter ? 'active' : ''}
          >
            <div className="count">{summary.processing}</div>
            <div className="label">Processing</div>
          </StatItem>
        )}

        {summary.review > 0 && (
          <StatItem
            $color={defaultColors.review}
            $clickable={summary.review > 0}
            onClick={() => summary.review > 0 && onFilterChange('status', 'review')}
            className={isFilterActive('status', 'review') || unifiedProcessingReviewFilter ? 'active' : ''}
          >
            <div className="count">{summary.review}</div>
            <div className="label">Review</div>
          </StatItem>
        )}

        {decisions.map(decision => {
          const count = summary[decision.key];
          if (count === 0 && !decision.showInProgress) return null;
          
          const isChange = decision.key === 'change' && enableChangeGrouping;
          
          return (
            <StatItem
              key={decision.key}
              $color={decision.color}
              $clickable={count > 0 || (isChange && hasChangeGroups)}
              onClick={(e) => {
                if (isChange && hasChangeGroups) {
                  handleChangeClick(e);
                } else if (count > 0) {
                  onFilterChange('decision', decision.key);
                }
              }}
              className={
                isFilterActive('decision', decision.key) || 
                (isChange && activeFilter?.type === 'change-concept') 
                  ? 'active' 
                  : ''
              }
            >
              <div className="count">
                {count}
                {isChange && hasChangeGroups && <DropdownIcon>▼</DropdownIcon>}
              </div>
              <div className="label">{decision.label}</div>
            </StatItem>
          );
        })}

        {customFeatures?.noDecision && summary.noDecision > 0 && (
          <StatItem
            $color="#9E9E9E"
            $clickable={true}
            onClick={() => onFilterChange('decision', 'no_decision')}
            className={isFilterActive('decision', 'no_decision') ? 'active' : ''}
          >
            <div className="count">{summary.noDecision}</div>
            <div className="label">No Decision</div>
          </StatItem>
        )}

        {summary.error > 0 && (
          <StatItem
            $color={defaultColors.error}
            $clickable={true}
            onClick={() => onFilterChange('status', 'error')}
            className={isFilterActive('status', 'error') ? 'active' : ''}
          >
            <div className="count">{summary.error}</div>
            <div className="label">Error</div>
          </StatItem>
        )}

        {customFeatures?.cannotLink && summary.cannotLink > 0 && (
          <StatItem
            $color={defaultColors.cannotLink}
            $clickable={true}
            onClick={() => onFilterChange('cannot-link', 'true')}
            className={isFilterActive('cannot-link', 'true') ? 'active' : ''}
          >
            <div className="count">{summary.cannotLink}</div>
            <div className="label">Cannot Link</div>
          </StatItem>
        )}
      </StatsCard>

      {enableChangeGrouping && ChangeGroupComponent && (
        <Popover
          open={changeOpen}
          anchorEl={changeAnchorEl}
          onClose={handleChangeClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <PopoverContent>
            <PopoverTitle>Changes by Concept:</PopoverTitle>
            {Object.values(changeGroups).map((group) => (
              <PopoverItem
                key={`${group.ref}_${group.refId}`}
                onClick={() => handleConceptFilterClick(group.ref, group.refId)}
                className={isFilterActive('change-concept', `${group.ref}_${group.refId}`) ? 'active' : ''}
              >
                <ChangeGroupComponent
                  conceptRef={group.ref}
                  refId={group.refId}
                  courseId={courseId}
                  showLabel={true}
                />
                <PopoverCount>{group.count}</PopoverCount>
              </PopoverItem>
            ))}
          </PopoverContent>
        </Popover>
      )}

    </Container>
  );
};

export default ValidatorSummary;

// Styled Components
const Container = styled.div`
  margin-bottom: 8px;
`;

const StatsCard = styled.div`
  display: flex;
  gap: 16px;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
`;

const StatItem = styled.div<{ $color?: string; $clickable?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  padding: 12px;
  border-radius: 6px;
  border: 2px solid transparent;
  transition: all 0.2s ease;
  cursor: ${(props) => (props.$clickable ? 'pointer' : 'default')};

  &:hover {
    background-color: ${(props) => (props.$clickable ? 'rgba(0, 0, 0, 0.03)' : 'transparent')};
  }

  &.active {
    background-color: rgba(33, 150, 243, 0.08);
    border-color: #2196f3;
  }

  .count {
    font-size: 28px;
    font-weight: 600;
    color: ${(props) => props.$color || '#333'};
    line-height: 1;
  }

  .label {
    font-size: 14px;
    color: #666;
    margin-top: 6px;
    font-weight: 500;
    text-align: center;
  }
`;

const ProgressBar = styled.div`
  height: 8px;
  background: #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
  margin: 0 0 10px 0;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.1);
  position: relative;
`;

const ProgressFill = styled.div<{ $width: number; $isProcessing?: boolean }>`
  height: 100%;
  width: ${(props) => props.$width}%;
  background: ${(props) =>
    props.$isProcessing
      ? 'linear-gradient(90deg, #9C27B0 0%, #7B1FA2 50%, #9C27B0 100%)'
      : 'linear-gradient(90deg, #66BB6A 0%, #42A5F5 50%, #FFA726 100%)'};
  transition: width 0.5s ease;
  position: relative;

  ${(props) =>
    props.$isProcessing &&
    `
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(
        90deg,
        transparent,
        rgba(255, 255, 255, 0.5),
        transparent
      );
      animation: shimmer 1s infinite;
    }
  `}

  @keyframes shimmer {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(100%);
    }
  }
`;

const ProgressSegment = styled.div<{
  $width: number;
  $color: string;
  $position: 'first' | 'middle' | 'last' | 'only';
  $offset?: number;
}>`
  position: absolute;
  height: 100%;
  width: ${(props) => props.$width}%;
  background: ${(props) => props.$color};
  left: ${(props) => props.$offset || 0}%;
  transition: all 0.5s ease;

  ${(props) => {
    if (props.$position === 'only') return 'border-radius: 4px;';
    if (props.$position === 'first') return 'border-radius: 4px 0 0 4px;';
    if (props.$position === 'last') return 'border-radius: 0 4px 4px 0;';
    return '';
  }}
`;

const DropdownIcon = styled.span`
  font-size: 10px;
  margin-left: 4px;
  vertical-align: middle;
`;

const PopoverContent = styled.div`
  padding: 15px;
  min-width: 250px;
  max-width: 400px;
`;

const PopoverTitle = styled.h4`
  margin: 0 0 10px 0;
  font-size: 14px;
  color: #666;
  border-bottom: 1px solid #eee;
  padding-bottom: 8px;
`;

const PopoverItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 8px;
  margin: 0 -8px;
  font-size: 14px;
  cursor: pointer;
  border-radius: 4px;

  &:hover {
    background-color: #f5f5f5;
  }

  &.active {
    background-color: #e3f2fd;
    border: 1px solid #2196f3;
  }
`;

const PopoverCount = styled.span`
  font-weight: bold;
  color: #1976d2;
  background-color: #e3f2fd;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
`;